import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { BRAND_NAME } from '../../config/brand';
import { FigureCoWordmark, FigureCoMonogram } from '../../components/brand/FigureCoLogo';
import { NavGroupData } from './useNavGroups';
import NavGroup from './NavGroup';

interface LeftRailProps {
  groups: NavGroupData[];
  activeRoute: string | null;
  onNavigate: (route: string) => void;
  userRole: string;
}

/**
 * LeftRail — Collapsible left navigation rail.
 *
 * Specs:
 * - 240px expanded / 64px collapsed
 * - Near-black background (var(--color-bg-base))
 * - Active nav item: 3px solid orange left border + white text + bg-elevated background
 * - Alert/badge counts: solid orange pill (bg-brand text-white)
 * - Collapse toggle button at rail bottom
 * - Persist collapse state in localStorage key 'ws_rail_collapsed'
 */
export default function LeftRail({ groups, activeRoute, onNavigate, userRole }: LeftRailProps) {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Initialize from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('ws_rail_collapsed');
      return saved === 'true';
    }
    return false;
  });

  // Persist collapse state
  useEffect(() => {
    localStorage.setItem('ws_rail_collapsed', String(isCollapsed));
  }, [isCollapsed]);

  const handleItemClick = (route: string | null) => {
    if (route) {
      onNavigate(route);
    }
  };

  return (
    <aside
      className={`
        h-screen sticky top-0 flex flex-col
        bg-[var(--color-bg-base)]
        border-r border-[var(--color-hairline)]
        transition-all duration-300 ease-in-out
        ${isCollapsed ? 'w-16' : 'w-[240px]'}
      `}
    >
      {/* Logo/Wordmark Section */}
      <div className="h-[58px] flex items-center px-4 border-b border-[var(--color-hairline)]">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          title={isCollapsed ? BRAND_NAME : undefined}
        >
          {isCollapsed ? (
            <span className="text-text-primary">
              <FigureCoMonogram size={26} />
            </span>
          ) : (
            <FigureCoWordmark size="sm" tagline={false} />
          )}
        </button>
      </div>

      {/* Navigation Groups */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {groups.map((group) => (
          <NavGroup
            key={group.id}
            group={group}
            isCollapsed={isCollapsed}
            activeRoute={activeRoute}
            onItemClick={handleItemClick}
          />
        ))}
      </nav>

      {/* User Role Badge - when collapsed show icon only */}
      <div className="px-2 py-2 border-t border-[var(--color-hairline)]">
        <div
          className={`
            flex items-center gap-2 px-2 py-1.5 rounded-md
            ${isCollapsed ? 'justify-center' : ''}
          `}
          title={`Role: ${userRole}`}
        >
          <span className="w-2 h-2 rounded-full bg-[var(--color-verified)] flex-shrink-0"></span>
          {!isCollapsed && (
            <span className="text-xs text-[var(--color-text-muted)] capitalize truncate">
              {userRole.replace(/_/g, ' ')}
            </span>
          )}
        </div>
      </div>

      {/* Collapse Toggle */}
      <div className="p-2 border-t border-[var(--color-hairline)]">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="
            w-full flex items-center justify-center gap-2
            px-2 py-2 rounded-md
            text-[var(--color-text-muted)]
            hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-secondary)]
            transition-colors
          "
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <>
              <ChevronLeft className="w-4 h-4" />
              <span className="text-xs">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
